import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles, BarChart3, Users2, Megaphone, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: Sparkles,
      title: 'Profile Optimization',
      description: 'Transform your LinkedIn profile into a conversion machine. Professional photography, compelling headlines, keyword optimization, and strategic positioning.',
      features: ['Professional copywriting', 'SEO optimization', 'Visual branding', 'Call-to-action strategy']
    },
    {
      icon: Megaphone,
      title: 'Content Strategy & Creation',
      description: 'Consistent, engaging content that positions you as an industry thought leader. Weekly posts, articles, and multimedia content tailored to your audience.',
      features: ['Content calendar', 'Industry insights', 'Engagement tactics', 'Multi-format content']
    },
    {
      icon: Target,
      title: 'Lead Generation Campaigns',
      description: 'Targeted outreach campaigns that connect you with decision-makers. Strategic messaging, personalized connection requests, and nurture sequences.',
      features: ['Prospect research', 'Personalized outreach', 'Follow-up automation', 'CRM integration']
    },
    {
      icon: BarChart3,
      title: 'Analytics & Optimization',
      description: 'Data-driven insights to continuously improve your LinkedIn performance. Monthly reports, competitor analysis, and strategic recommendations.',
      features: ['Performance tracking', 'Competitor analysis', 'A/B testing', 'ROI reporting']
    },
    {
      icon: Users2,
      title: 'Network Growth Strategy',
      description: 'Strategic network expansion to connect with your ideal clients and partners. Quality over quantity, focused on meaningful business relationships.',
      features: ['Audience targeting', 'Connection strategy', 'Relationship nurturing', 'Community building']
    }
  ];

  return (
    <div className="pt-16">
      <section className="py-32 bg-primary-dark text-text-inverse" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block text-secondary-main font-body uppercase tracking-widest text-xs mb-4"
            data-testid="services-subtitle"
          >
            Our Services
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl lg:text-6xl font-bold mb-6 leading-tight"
            data-testid="services-title"
          >
            Comprehensive LinkedIn Marketing Solutions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-text-inverse/80 text-lg max-w-3xl mx-auto leading-relaxed"
            data-testid="services-description"
          >
            From profile optimization to full-scale lead generation campaigns, we provide end-to-end LinkedIn marketing services designed to grow your business.
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-background-main" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-primary-main/10 p-8 hover:shadow-xl transition-all"
                  data-testid={`service-card-${index}`}
                >
                  <Icon className="w-12 h-12 text-secondary-main mb-4" data-testid={`service-icon-${index}`} />
                  <h3 className="font-heading text-2xl font-bold text-primary-main mb-3" data-testid={`service-title-${index}`}>
                    {service.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed mb-6" data-testid={`service-description-${index}`}>
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2" data-testid={`service-feature-${index}-${fIndex}`}>
                        <CheckCircle2 className="w-5 h-5 text-secondary-main flex-shrink-0 mt-0.5" />
                        <span className="text-text-secondary text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-32 bg-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl lg:text-5xl font-bold text-primary-main mb-6" data-testid="cta-heading">
            Ready to Transform Your LinkedIn Presence?
          </h2>
          <p className="text-text-secondary text-lg leading-relaxed mb-8" data-testid="cta-description">
            Let's discuss how our services can help you achieve your business goals.
          </p>
          <Link
            to="/enquiry"
            data-testid="cta-button"
            className="bg-primary-main text-white hover:bg-primary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-block transition-all"
          >
            Schedule a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;